<template>
  <div class="scheduler-container">
    <unit-modal ref="unitModal" />
    <unit-type-modal ref="unitTypeModal" />
    <DayPilotScheduler
      ref="scheduler"
      id="dp"
      :config="config"
      :resources="resources"
    />
    <div class="scheduler-date-tools">
      <b-button class="scheduler-today-button" @click="scrollToToday">
        {{ $t("scheduler.today_button").toUpperCase() }}
      </b-button>
      <i class="scheduler-cal-icon fi-calendar" />
      <v-date-picker
        v-model="selectedDate"
        class="scheduler-date-picker"
        mode="single"
        show-caps
      />
    </div>
  </div>
</template>

<script>
import UnitModal from "@/components/modals/UnitModal";
import UnitTypeModal from "@/components/modals/UnitTypeModal";
import PlusMinusButton from "@/components/widgets/PlusMinusButton";
import { formatBookingPeriod } from "@/utils/date-helper";
import { BOOKING_STATUS } from "@/config/bookings";
import { DayPilot, DayPilotScheduler } from "daypilot-pro-vue";
import { mapGetters, mapActions } from "vuex";
const borderClasses = ["vip-border", "employee-border", "maintentance-border"];

export default {
  name: "Scheduler",
  props: {
    units: {
      type: Array,
      required: true,
      default: () => [],
    },
    reservations: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  events: ["timeRangeSelected", "reservationClicked"],
  data: function () {
    return {
      DEBUG: true,
      selectedDate: new Date(Date.now()),
      schedulerLocales: {
        en: "en-us",
        es: "es-es",
        xx: "es-es",
      },
      schedulerDayShortNames: {
        en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        es: ["Dom.", "Lun.", "Mar.", "Mié.", "Jue.", "Vie.", "Sáb."],
        xx: ["Dom.", "Lun.", "Mar.", "Mié.", "Jue.", "Vie.", "Sáb."],
      },
      config: {
        timeHeaders: [
          {
            // TODO: add class with DayPilot.Scheduler.onBeforeTimeHeaderRender(args)
            groupBy: "Month",
          },
          {
            groupBy: "Day",
            format: "ddd",
          },
          {
            groupBy: "Day",
            format: "d",
          },
        ],
        scale: "Day",
        scrollTo: DayPilot.Date.today(),
        // floatingTimeHeaders: true,
        theme: "scheduler_white",
        cellGroupBy: "Month",
        days: 90,
        cellDuration: 1440,
        moveBy: "Full",
        dynamicLoading: false,
        infiniteScrollingEnabled: true,
        allowEventOverlap: false,
        dynamicEventRendering: "Enabled",
        eventMovingStartEndEnabled: true,
        crosshairType: "full",
        eventHoverHandling: "Bubble",
        treeEnabled: true,
        rowHeaderWidth: 200,
        rowHeaderWidthAutoFit: false,
        eventHeight: 54,
        cellWidth: 54,
        treePreventParentUsage: true,
        headerHeight: 18,
        headerDateFormat: "dddd",
        heightSpec: "Parent100Pct",
        rowHeaderWidthAutoFit: false,
        progressiveRowRendering: true,
        // scrollDelayDynamic: 10,
        // Interaction Callbacks
        onTimeRangeSelected: this.dpOnTimeRangeSelected,
        onEventClicked: this.dpOnEventClicked,
        //onResourceHeaderClick: this.editRoom,
        onEventMoved: this.dpOnEventDateChanged,
        onEventResized: this.dpOnEventDateChanged,
        onEventResizing: this.onEventResizing,
        onEventMoving: this.onEventResizing,
        onTimeRangeSelecting: this.onTimeRangeSelecting,
        // Rendering Callbacks
        onBeforeRowHeaderRender: this.dpOnBeforeRowHeaderRender,
        onBeforeEventRender: this.dpOnBeforeEventRender,
        // onBeforeCellRender: this.dpOnBeforeCellRender
      },
    };
  },
  components: {
    UnitModal,
    UnitTypeModal,
    PlusMinusButton,
    DayPilotScheduler,
  },
  computed: {
    ...mapGetters(["getContact", "getUnitById", "getUnits", "getBooking"]),
    // DayPilot.Scheduler object - https://api.daypilot.org/daypilot-scheduler-class/
    scheduler: function () {
      return this.$refs.scheduler.control;
    },
    resources: function () {
      let retVal = [];
      const lUnits = this.units;
      for (let i = 0; i < this.units.length; i++) {
        // let unitType = this.units[i];
        // let e = {
        //   name: unitType.unit_type_name,
        //   id: "UnitTypeCell",
        //   expanded: true,
        //   children: [],
        //   tags: unitType
        // };
        // for (let j = 0; j < unitType.unit.length; j++) {
        //   let unit = unitType.unit[j];
        //   let eChild = {
        //     name: unit.unit_name,
        //     id: unit.id,
        //     tags: unit
        //   };
        //   e.children.push(eChild);
        // }
        // if (e.children.length > 0) {
        //   retVal.push(e);
        // }

        let unitType = this.units[i];
        for (let j = 0; j < unitType.unit.length; j++) {
          let unit = unitType.unit[j];
          let e = {
            name: unit.unit_name,
            id: unit.id,
            tags: { unit: unit, unitType: unitType },
          };
          retVal.push(e);
        }
      }
      return retVal;
    },
    events: function () {
      const validUnits = this.getUnits;
      const validEvents = this.reservations.filter((reservation) => {
        return (
          validUnits.map((unit) => unit.id).indexOf(reservation.unit_id) > -1
        );
      });
      const events = validEvents.map((reservation) => {
        let imageUri = null;
        const booking = this.getBooking(reservation.booking_id);
        const contact = this.getContact(booking.guest_id);
        if (contact && contact.photo_uri) {
          imageUri =
            contact.photo_sm_uri || contact.photo_md_uri || contact.photo_uri;
        }
        if (!reservation.unit) {
          reservation.unit = this.getUnitById(reservation.unit_id);
        }
        let ring_class = "";
        let indicator_class = "";
        if (booking.booking_status_id === BOOKING_STATUS.CHECKED_OUT) {
          if (booking.is_paid) {
            ring_class = "ring-grey";
          } else {
            ring_class = "ring-red";
          }
        } else {
          if (booking.paid_deposit > 0) {
            ring_class = "ring-blue";
          } else {
            ring_class = "ring-yellow";
          }
          if (booking.booking_status_id === BOOKING_STATUS.CHECKED_IN) {
            indicator_class = "solid-circle";
          } else {
            let today = this.$moment().format("YYYY-MM-DD");
            let startDate = this.$moment(reservation.starts_at).format(
              "YYYY-MM-DD"
            );
            if (today >= startDate) {
              indicator_class = "open-circle";
            }
          }
        }
        return {
          id: reservation.id,
          text: `${reservation.booking_name}`,
          start: new DayPilot.Date(reservation.starts_at),
          end: new DayPilot.Date(reservation.ends_at),
          resource: reservation.unit_id,
          unit_type: reservation.unit.unit_type_id,
          image_uri: imageUri,
          ring_class: ring_class,
          indicator_class: indicator_class,
          notes: booking.notes,
          ota_name: booking.ota_name,
          ota_reservation_code: booking.ota_reservation_code,
        };
      });
      return events;
    },
  },
  methods: {
    ...mapActions(["getFilteredTab"]),
    // Peripheral Functions
    editRoom(_record) {
      let unit = _record.resource.data.tags;
      if ("unit" in unit) {
        //this.$refs.unitTypeModal.edit(unit);
        //} else {
        this.$refs.unitModal.edit(unit.unit_type_id, unit);
      }
    },
    // DayPilot Event Functions
    dpOnTimeRangeSelected: function (_args) {
      if (_args.resource === "UnitTypeCell") {
        return;
      }
      const unit = this.getUnitById(_args.resource);
      const event = {
        starts_at: _args.start.value,
        ends_at: _args.end.value,
        unit_id: unit.id,
        unit_rate: unit.unit_rate,
      };
      this.scheduler.clearSelection();
      this.$emit("renderBookingForm", event);
    },
    addDateBubbles: function (args) {
      let startDateString = args.start.toString("M/d");
      let endDateString = args.end.toString("M/d");
      args.right.enabled = true;
      args.left.enabled = true;
      args.left.html =
        "<span class='check-in-label'><span class='check-out-text'>Check In: " +
        startDateString +
        "</span></span>";
      args.right.html =
        "<span class='check-out-label'><span class='check-out-text'>Check Out: " +
        endDateString +
        "</span></span>";
    },
    onTimeRangeSelecting: function (args) {
      this.addDateBubbles(args);
    },
    onEventResizing: function (args) {
      this.addDateBubbles(args);
    },
    dpOnEventClicked: function (_args) {
      const reservation = this.$store.getters.getReservations.find(
        (reservation) => reservation.id === _args.e.id()
      );
      this.$emit("renderBookingForm", reservation);
    },
    dpOnEventDateChanged: function (_args) {
      const updates = {};
      updates.starts_at = this.$moment(_args.e.toJSON().start).format(
        "YYYY-MM-DD"
      );
      updates.ends_at = this.$moment(_args.e.toJSON().end).format("YYYY-MM-DD");
      updates.unit_id = _args.e.resource();

      const originalReservation = this.$store.getters.getReservationsDeep.find(
        (reservation) => reservation.id === _args.e.id()
      );

      const updatedReservation = {
        ...originalReservation,
        ...updates,
      };

      this.$store
        .dispatch("updateReservation", updatedReservation)
        .then((res) => {
          this.scheduler.update({
            events: this.events,
          });
        })
        .catch((err) => {
          this.errorNotification("We were unable to update this reservation");
        });
    },
    // DayPilot Rendering Functions
    dpOnBeforeEventRender: function (_args) {
      if (!Object.keys(_args.data).length) {
        return;
      }
      // Get the guest's info here and properly access the image path
      const picturePlaceholder =
        require("@/assets/images/portrait_placeholder.png").default;

      // Determine border class based on reservation type
      let borderClass = "vip-border"; // default border
      const booking = this.getBooking(_args.data.booking_id);
      if (booking && booking.is_employee) {
        borderClass = "employee-border";
      } else if (booking && booking.is_maintenance) {
        borderClass = "maintenance-border";
      }

      let classes = "";
      if (_args.data.start) {
        classes = `reservation-event resource-${_args.data.resource} date-${
          _args.data.start.value.split("T")[0]
        }`;
      }

      let otaClass = "";
      if (_args.data.ota_name) {
        otaClass = `reservation-event--${_args.data.ota_name.toLowerCase()}`;
      }

      let ringClass = _args.data.ring_class;

      // Extract the name from the text for generating initials
      const guestName = _args.data.text || "";

      // Generate initials using the same logic as PicturePlaceholder
      let initials = "";
      if (guestName) {
        const acronym = guestName
          .split(/\s/)
          .reduce((response, word) => (response += word.slice(0, 1)), "");
        initials =
          String(acronym.charAt(0)) +
          String(acronym.length > 1 ? acronym.charAt(acronym.length - 1) : "");
      }

      // Decide whether to show initials or image
      const hasImage = _args.data.image_uri && _args.data.image_uri !== "";
      let eventHtml;

      if (!hasImage) {
        // Use initials placeholder when there's no image
        // Structure matches the img version as closely as possible
        eventHtml = `<div class="${classes} ${_args.data.indicator_class} ${ringClass} ${otaClass}">
            <span class="event-image ${borderClass} initials-placeholder">${initials}</span>
            <div class="${classes}">
              <div class="reservation-event-label">
                ${_args.data.text}
              </div>
            </div>
          </div>`;
      } else {
        // Original image-based rendering (kept for reference)
        eventHtml = `<div class="${classes} ${
          _args.data.indicator_class
        } ${ringClass} ${otaClass}">
            <img src="${
              _args.data.image_uri || picturePlaceholder
            }" class="event-image ${borderClass}"/>
            <div class="${classes}">
              <div class="reservation-event-label">
                ${_args.data.text}
              </div>
            </div>
          </div>`;
      }

      let statusText = "";
      if (ringClass === "ring-yellow") {
        statusText = "No payments";
      } else if (ringClass === "ring-blue") {
        statusText = "Payment made to tab";
      } else if (ringClass === "ring-red") {
        statusText = "Checked out with outstanding balance";
      } else if (ringClass === "ring-grey") {
        statusText = "Checked out";
      }
      _args.data.html = eventHtml;
      _args.data.bubbleHtml = `<div class="event-hover-bubble" style="max-width:400px;">
            <h3 class="bubble-title">${_args.data.text}</h3>
            <p class="bubble-body"">
              <span class="bubble-body--label" style="font-weight: bold;">
                Check In:
              </span>
              <span class="bubble-boday--value">
                ${this.$moment(_args.data.start.value).format(
                  "MM/DD/YYYY"
                )} - <b>Check Out:</b> ${this.$moment(
        _args.data.end.value
      ).format("MM/DD/YYYY")}
              </span>
            </p>
            <p class="bubble-body">
              <span class="bubble-body--label" style="font-weight: bold;">
                Status:
              </span>
              <span class="bubble-boday--value">
                ${statusText}
              </span>
            </p>`;

      if (_args.data.notes) {
        _args.data.bubbleHtml += `<p class="bubble-body">
              <span class="bubble-body--label"  style="font-weight: bold;">
                Notes:
              </span>
              <span class="bubble-body--value bubble-body--notes">
                ${_args.data.notes}
              </span>
            </p>`;
      }

      if (_args.data.ota_name) {
        _args.data.bubbleHtml += `<p class="bubble-body">
              <span class="bubble-body--label"  style="font-weight: bold;">
                Booking source:
              </span>
              <span class="bubble-body--value bubble-body--notes">
                ${_args.data.ota_name}
              </span>
            </p>`;
      }

      _args.data.bubbleHtml += `</div>`;
    },
    dpOnBeforeRowHeaderRender: function (_args) {
      if (_args.row.value == "UnitTypeCell") {
        _args.row.cssClass = "scheduler-unit-category-row";
      }

      let unitTypeName = _args.row.tags.unitType.unit_type_name;
      let unitName = _args.row.tags.unit.unit_name;
      let html = "<div><div class='row-header-unit-label'>";

      html += unitName;
      html += "</div><div class='row-header-unit-type-label'>";
      html += unitTypeName.toUpperCase();
      html += "</div></div>";

      _args.row.html = html;
    },
    dpOnBeforeCellRender: function (_args) {
      if (Number.isInteger(_args.cell.resource)) {
        _args.cell.cssClass = `available-date resource-${
          _args.cell.resource
        } date-${_args.cell.start.value.split("T")[0]}`;
      }
      // let values = [0, 0.5, 0];
      // console.dir(_args.cell);
      // if (_args.cell.isParent > 0) {
      //   // console.dir(_args.cell.events());
      //   // console.dir(_args);
      //   let id = _args.cell;
      //   let fillHeight = (54 * values[1]);
      //   let fillTop = 54 - fillHeight;
      //   // let svgHtml = '<svg width="54" height="54">';
      //   // svgHtml += '<rect x="15" y="' + fillTop + '" width="24" height="' + fillHeight + '" fill="#999"/>'
      //   // svgHtml +=    '</svg>';
      //   // _args.cell.html = svgHtml;
      // } else {
      //   // _args.cell.html = '  <div class="plus_icon_container"><div class="plus_icon"></div></div>';
      // }
    },
    scrollToDate: function (_dateString) {
      this.scheduler.scrollTo(new DayPilot.Date(_dateString));
      // this.scheduler.update();
    },
    scrollToToday: function () {
      this.scheduler.scrollTo(DayPilot.Date.today());
    },
    update: function () {
      this.scheduler.update({
        resources: this.resources,
        events: this.events,
      });
    },
  },
  mounted: function () {
    const locale = localStorage.getItem("locale") || "en";
    if (this.scheduler && this.schedulerLocales[locale]) {
      this.scheduler.locale = this.schedulerLocales[locale];

      // Only set dayNamesShort if the locale exists in DayPilot
      if (DayPilot.Locale.all[this.scheduler.locale]) {
        DayPilot.Locale.all[this.scheduler.locale].dayNamesShort =
          this.schedulerDayShortNames[locale];
      }
    }
    this.update();
  },
  watch: {
    selectedDate() {
      this.scrollToDate(this.selectedDate);
    },
    resources() {
      this.update();
    },
  },
};
</script>

<style lang="scss">
@import "@/style/_scheduler.scss";
</style>
